import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { readFirst } from '@dg/shared-rxjs';

import { emitOnce } from '@ngneat/elf';
import {
  deleteAllEntities,
  resetActiveIds,
  upsertEntities,
} from '@ngneat/elf-entities';
import {
  deleteAllPages,
  selectCurrentPageEntities,
  setCurrentPage,
  setPage,
  updatePaginationData,
} from '@ngneat/elf-pagination';

import {
  initStoreState,
  PaginatedStore,
  PaginationProps,
} from '@app/shared/rsm';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';

import {
  SearchState,
  LearningResource,
  SearchLearningResponse,
  SearchChangeTrigger,
} from './models/search.model';
import { updateRequestStatus, getIsReady } from '@app/shared/rsm/utils';
import { initSearchState, PAGINATION_LIMIT } from './utils';

const SEARCH_LEARNINGS = 'searchLearnings';

/**
 * Reactive store for "Search Learning Resources"
 */
@Injectable()
export class SearchStore extends PaginatedStore<SearchState, LearningResource> {
  public state$: Observable<SearchState>;

  private learnings$: Observable<LearningResource[]>;

  constructor() {
    super(SEARCH_LEARNINGS, initSearchState);

    this.learnings$ = this._store.pipe<LearningResource[]>(
      selectCurrentPageEntities()
    );

    this.state$ = this._store.pipe(
      map((state) => {
        const results = readFirst<LearningResourceViewModel[]>(this.learnings$);
        const pagination = readFirst<PaginationProps>(this.pagination$);

        // Unified store status for UI access;
        // Note:  aggregates pending, initializing, error (showSkeleton)
        const isReady = getIsReady(state);
        const isLoading = !isReady;

        return {
          ...state,
          isReady,
          isLoading,
          results,
          pagination,
        };
      }),
      shareReplay({ refCount: true })
    );
  }

  /**********************************************
   * Store Methods
   **********************************************/

  /**
   * When a new search occurs and the store needs to be reset with full
   * pagination information. This should not be used to navigate pages nor
   * add more pages (retrieved with the same search criteria)
   */
  public updateLearnings(
    response: SearchLearningResponse,
    partialUpdates: Partial<SearchState>,
    reset = false
  ) {
    const learnings = response.results;
    const pagination = {
      ...readFirst<PaginationProps>(this.pagination$),
      ...response.pagination,
      currentPage: response.pagination.currentPage || 1,
      perPage: PAGINATION_LIMIT,
    };
    const updateState = (state: SearchState): SearchState => ({
      ...state,
      term: response.term,
      filters: response.filters,
      sort: partialUpdates.sort,
      orgId: partialUpdates.orgId,
      isExternalCat: partialUpdates.isExternalCat,
      isMarketplaceCat: partialUpdates.isMarketplaceCat,
      appliedFacets: partialUpdates.appliedFacets,
      suggestedTerm: response.suggestedTerm,
      typoAutoSearched: response.typoAutoSearched,
      previousTerm: response.previousTerm,
      changeTrigger: partialUpdates.changeTrigger,
      viewLearningId: partialUpdates.viewLearningId,
    });

    emitOnce(() => {
      const initState = () => ({ ...initSearchState(), ...initStoreState() });

      if (reset) {
        this._store.update(
          initState,
          resetActiveIds(),
          deleteAllEntities(),
          deleteAllPages()
        );
      }

      this._store.update(
        updateState,
        upsertEntities(learnings),
        updatePaginationData(pagination),
        setPage(
          pagination.currentPage,
          learnings.map((it) => it.id)
        ),
        resetActiveIds(),
        setCurrentPage(pagination.currentPage),
        updateRequestStatus(SEARCH_LEARNINGS, 'success')
      );
    });
  }

  /**
   * Use in place of `PaginatedStore.selectPage` to add tracking property for cached page changes
   */
  public selectPage(page: number): boolean {
    const found = this.hasPage(page);
    if (found) {
      const updateState = (state: SearchState) => ({
        ...state,
        changeTrigger: SearchChangeTrigger.Page,
      });
      emitOnce(() => {
        this._store.update(updateState, setCurrentPage(page));
      });
    }

    return found;
  }

  /**
   * Retain the search term on a failed search
   *
   * @param partialUpdate
   */
  public handleError(partialUpdate: Partial<SearchState>) {
    const updateState = (state: SearchState) => {
      const term = partialUpdate.term || state.term;
      return { ...state, term, isLoading: false };
    };

    emitOnce(() => {
      this._store.update(
        updateState,
        updateRequestStatus(SEARCH_LEARNINGS, 'error')
      );
    });
  }
}
